/**
 * CalendarMobile.js
 * Versão mobile dedicada do Calendar.js.
 * Grid de horas × dias otimizado para telas pequenas.
 * slotHeight: dinâmico (36–60px) | TimeAxis: 32px | DayColumn min-width: 72px
 *
 * Os cards são os mesmos do computador (AppointmentCard.js): o que aparece
 * depende da largura do card, igual em qualquer tela.
 */

import React, { useMemo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import AppointmentsState from '../../../consts/AppointmentsState';
import { useAttendTourTarget } from '../../../components/guided-tour/GuidedTourContext';
import { SLOT_INTERVAL_MINUTES as SLOT_INTERVAL_MINUTES_DEFAULT } from '../../../consts/CalendarConfig';
import { getNationalHoliday } from '../../../utils/brazilianHolidays';
import { Tooltip } from '@mui/material';
import {
  groupOverlapping,
  fitNonBlockers,
  blockerCardEdges,
  nonBlockerRightOffset,
} from './slotLayout';
import { AppointmentCard, NonBlockerCard } from './AppointmentCard';
import {
  belongsToColumn, buildResourceColumns, columnDayProps, columnResourceId,
  columnHasOwnItems, IDLE_TO_END_MIN_COLUMNS, UNASSIGNED_COLUMN_ID,
} from './resourceColumns';
import DayRoomChip from '../../../components/day-rooms/DayRoomChip';
import { offHoursRanges } from '../../../utils/resourceSchedule';

// ─── Constantes ───────────────────────────────────────────────────────────────
const MIN_SLOT_HEIGHT = 36;
const MAX_SLOT_HEIGHT = 60;
const DAY_HEADER_HEIGHT = 48;
const DEFAULT_SLOT_HEIGHT = 44;
// Largura mínima da coluna de dia (CSS inline abaixo) — usada como palpite
// seguro até o ResizeObserver medir a largura real.
const DAY_COLUMN_MIN_WIDTH = 72;
// Modo "Profissionais": coluna mais larga, com rolagem lateral entre profissionais
const RESOURCE_COLUMN_MIN_WIDTH = 132;

// Constantes de layout horizontal do slot (mobile):
//   STRIP_PX              faixa do "+" colada à direita — SEMPRE preservada
//   NON_BLOCKER_WIDTH_PX  largura da faixa de reagendado/cancelado/falta
//   MIN_BLOCKER_WIDTH_PX  largura mínima legível de um card bloqueante
const SLOT_LAYOUT_MOBILE = {
  STRIP_PX: 14,
  NON_BLOCKER_WIDTH_PX: 24,
  MIN_BLOCKER_WIDTH_PX: 28,
  GAP_PX: 2,
};

// Destaque do agendamento recém-salvo (mesmo rosa do indicador de hora atual)
const HIGHLIGHT_SHADOW = '0 0 0 2px #ED3680, 0 6px 16px rgba(237, 54, 128, 0.35)';
// Largura do eixo de horas (sticky): a coluna escolhida rola para logo depois dele
const TIME_AXIS_WIDTH_PX = 32;
// Quanto tempo a coluna fica destacada depois de "Localizar profissional"
const COLUMN_HIGHLIGHT_MS = 1500;

// O id pode não vir no retorno do salvar; nesse caso casa por data + paciente
function isHighlighted(appointment, highlighted) {
  if (!highlighted || !appointment) return false;
  if (highlighted.id != null) return String(appointment.id) === String(highlighted.id);
  return appointment.date === highlighted.date && String(appointment.patient) === String(highlighted.patient);
}

// ─── Calcula geometria (top + height) de um agendamento ──────────────────────
function calcCardGeometry(appointment, startHour, slotHeight, slotIntervalMinutes) {
  const date = new Date(appointment.date);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const topPx = (hours - startHour + minutes / 60) * slotHeight;

  let heightPx = slotHeight * (slotIntervalMinutes / 60); // padrão = intervalo configurado
  if (appointment.duration) {
    const [dh, dm] = appointment.duration.split(':').map(Number);
    const durationHours = dh + dm / 60;
    heightPx = Math.max(durationHours * slotHeight, 24);
  }
  return { topPx, heightPx };
}

// ─── Indicador de hora atual ──────────────────────────────────────────────────
const CurrentTimeIndicator = ({ startHour, endHour, slotHeight }) => {
  const [topPx, setTopPx] = useState(0);
  const [timeLabel, setTimeLabel] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      const currentTotalMinutes = h * 60 + m;
      const startTotalMinutes = startHour * 60;
      const endTotalMinutes = endHour * 60;
      setIsVisible(currentTotalMinutes >= startTotalMinutes && currentTotalMinutes <= endTotalMinutes);
      setTopPx((h - startHour + m / 60) * slotHeight);
      setTimeLabel(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [startHour, endHour, slotHeight]);

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'absolute',
      left: 0,
      right: 0,
      top: topPx,
      height: '2px',
      background: '#f48fb1',
      pointerEvents: 'none',
      zIndex: 10,
    }}>
      {/* Círculo marcador — rosa claro sólido */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: '9px',
        height: '9px',
        borderRadius: '50%',
        background: '#f48fb1',
      }} />
      {/* Label de hora — fundo rosa claro, texto rosa escuro */}
      <span style={{
        position: 'absolute',
        left: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '9px',
        fontWeight: 700,
        color: '#ad1457',
        background: '#fce4ec',
        padding: '1px 4px',
        borderRadius: '3px',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        zIndex: 11,
      }}>
        {timeLabel}
      </span>
    </div>
  );
};

// ─── Eixo de horários (sticky à esquerda) ────────────────────────────────────
const TimeAxisMobile = ({ startHour, endHour, slotHeight }) => {
  const totalHours = endHour - startHour;
  const totalHeight = totalHours * slotHeight;

  return (
    <div style={{
      width: '32px',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#FFFFFF',
      borderRight: '1px solid #F1F5F9',
      position: 'sticky',
      left: 0,
      zIndex: 10,
    }}>
      {/* Espaço vazio alinhado com o header das colunas de dia */}
      <div style={{
        height: '48px',
        flexShrink: 0,
        borderBottom: '1px solid #F1F5F9',
        background: '#FFFFFF',
      }} />

      {/* Corpo do eixo de horas */}
      <div style={{
        position: 'relative',
        height: `${totalHeight}px`,
        overflow: 'visible',
      }}>
        {Array.from({ length: totalHours }, (_, i) => (
          <div
            key={startHour + i}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${i * slotHeight}px`,
              height: `${slotHeight}px`,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              paddingTop: '3px',
              paddingRight: '4px',
            }}
          >
            <span style={{
              fontSize: '9px',
              fontWeight: 500,
              color: '#9CA3AF',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}>
              {String(startHour + i).padStart(2, '0')}h
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Coluna de um dia ─────────────────────────────────────────────────────────
const DayColumnMobile = ({
  date,
  appointments,
  startHour,
  endHour,
  slotHeight,
  onAppointmentClick,
  onEmptySlotClick,
  today,
  isNonWorkingDay = false,
  blockedHours = [],
  // Faixas do profissional no dia, em minutos (null = segue a empresa)
  ownHours = null,
  slotIntervalMinutes = SLOT_INTERVAL_MINUTES_DEFAULT,
  highlightedAppointment = null,
  // Modo "Profissionais": a coluna é de um profissional (nome no cabeçalho)
  resourceLabel,
  resourceId = null,
  // Sala do dia da agenda (chip só leitura sob o nome)
  resourceRoom,
  // O card não repete o nome do profissional (a coluna já diz de quem é)
  hideResource = false,
  // Destaque passageiro depois de "Localizar profissional"
  highlighted = false,
}) => {
  const isResourceColumn = resourceLabel !== undefined;
  const minColumnWidth = isResourceColumn ? RESOURCE_COLUMN_MIN_WIDTH : DAY_COLUMN_MIN_WIDTH;
  const isToday = date.toDateString() === today.toDateString();
  const tourTarget = useAttendTourTarget();
  // Feriado nacional: só aviso no cabeçalho, a grade continua livre para marcar
  const holiday = getNationalHoliday(date);

  const totalHours = endHour - startHour;
  const totalHeight = totalHours * slotHeight;

  const grouped = useMemo(() => groupOverlapping(appointments), [appointments]);

  // ── Largura real da coluna ────────────────────────────────────────────────
  // Medida com ResizeObserver (fallback: getBoundingClientRect no mount + resize
  // da janela). Até medir, assume o min-width para que a primeira pintura já
  // seja segura. A largura alimenta fitNonBlockers, que decide quantas faixas
  // de non-blocker cabem sem espremer os cards bloqueantes.
  const dayBodyRef = useRef(null);
  const [columnWidthPx, setColumnWidthPx] = useState(minColumnWidth);

  useEffect(() => {
    const el = dayBodyRef.current;
    if (!el) return undefined;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect && rect.width > 0) setColumnWidthPx(rect.width);
    };
    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    return undefined;
  }, []);


  // Decide quantas faixas de non-blocker cabem de fato na largura medida
  const laidOut = useMemo(
    () => fitNonBlockers(grouped, columnWidthPx, SLOT_LAYOUT_MOBILE),
    [grouped, columnWidthPx]
  );

  // Agendamento recém-salvo: rola até o cartão assim que ele aparece na coluna
  const highlightRef = useRef(null);
  useEffect(() => {
    if (!highlightedAppointment || !highlightRef.current) return;
    if (typeof highlightRef.current.scrollIntoView !== 'function') return;
    highlightRef.current.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
  }, [highlightedAppointment, laidOut]);

  // Conjunto de horas inteiras bloqueadas para lookup rápido O(1)
  const blockedHourSet = useMemo(() => new Set((blockedHours || []).map(h => Math.floor(h))), [blockedHours]);

  // ── Hover visual de slot (touch + mouse) ──────────────────────────────────
  const [hoveredStrip, setHoveredStrip] = useState(null);

  function calcSlotFromY(rect, clientY) {
    const offsetY = clientY - rect.top;
    const totalMinutes = (offsetY / slotHeight) * 60;
    const hour = startHour + Math.floor(totalMinutes / 60);
    const minute = Math.floor((totalMinutes % 60) / slotIntervalMinutes) * slotIntervalMinutes;
    return { hour, minute };
  }

  function handleBodyMouseMove(e) {
    if (e.target.closest('[data-card]')) { setHoveredStrip(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const { hour, minute } = calcSlotFromY(rect, e.clientY);
    setHoveredStrip(`${hour}-${minute}`);
  }

  function handleBodyMouseLeave() {
    setHoveredStrip(null);
  }

  function handleBodyTouchMove(e) {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (el && el.closest('[data-card]')) { setHoveredStrip(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const { hour, minute } = calcSlotFromY(rect, touch.clientY);
    setHoveredStrip(`${hour}-${minute}`);
  }

  function handleBodyTouchEnd() {
    setHoveredStrip(null);
  }

  function handleBodyClick(e) {
    if (!onEmptySlotClick) return;

    // Cards usam e.stopPropagation() no onClick, então se chegou aqui
    // o clique foi em espaço vazio — seguro criar novo agendamento.

    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const totalMinutes = (offsetY / slotHeight) * 60;
    const clickedHour = startHour + Math.floor(totalMinutes / 60);
    const clickedMinute = Math.floor((totalMinutes % 60) / slotIntervalMinutes) * slotIntervalMinutes;

    const clickedDate = new Date(date);
    clickedDate.setHours(clickedHour, clickedMinute, 0, 0);

    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${clickedDate.getFullYear()}-${pad(clickedDate.getMonth() + 1)}-${pad(clickedDate.getDate())} ${pad(clickedDate.getHours())}:${pad(clickedDate.getMinutes())}:00`;

    // Calcula a posição do slot clicado para ancorar o popover (igual ao desktop)
    const slotTopInBody = (clickedHour - startHour + clickedMinute / 60) * slotHeight;
    const slotRect = {
      left: rect.left,
      top: rect.top + slotTopInBody,
      right: rect.right,
      bottom: rect.top + slotTopInBody + slotHeight * (slotIntervalMinutes / 60),
      width: rect.width,
      height: slotHeight * (slotIntervalMinutes / 60),
    };

    // Passa evento enriquecido com _slotRect para o pai ancorar o popover ao slot
    const anchoredEvent = { ...e, clientX: slotRect.left, clientY: slotRect.top, _slotRect: slotRect };
    const slotData = { status: AppointmentsState.Available, date: dateStr };
    if (isResourceColumn && resourceId != null) slotData.resource = resourceId;
    onEmptySlotClick(slotData, anchoredEvent);
  }

  return (
    <div
      data-resource-col={isResourceColumn && resourceId != null ? resourceId : undefined}
      style={{
        minWidth: `${minColumnWidth}px`,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid #F8FAFC',
        // Mesmo destaque do computador: fundo rosa claro e borda interna
        background: highlighted ? '#FFF0F7' : 'transparent',
        boxShadow: highlighted ? 'inset 0 0 0 2px #FBCFE8' : 'none',
        transition: 'background 0.4s ease, box-shadow 0.4s ease',
      }}
    >
      {/* Cabeçalho do dia — layout light */}
      <div style={{
        height: '48px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: '1px solid #E8ECF0',
        // Coluna de profissional: azul claro (todas são o mesmo dia); coluna de
        // data: rosa só no dia de hoje
        background: highlighted ? '#FFF0F7'
          : isResourceColumn ? '#EEF3FF'
            : isToday ? '#FFF0F7' : (holiday ? '#FFFBEB' : '#fff'),
        cursor: 'default',
        gap: '2px',
        padding: '0 4px',
        boxSizing: 'border-box',
      }}
      >
        {/* Selo de feriado no canto com o nome (truncado se faltar espaço); o popup
            mostra o nome completo ao tocar. Fica acima do conteúdo, que não se move */}
        {holiday && (
          <Tooltip title={`Feriado: ${holiday}`} arrow placement="bottom-start" enterTouchDelay={0} leaveTouchDelay={3000}>
            <span style={{
              position: 'absolute',
              top: '1px',
              left: '3px',
              maxWidth: 'calc(100% - 6px)',
              boxSizing: 'border-box',
              fontSize: '7px',
              fontWeight: 600,
              lineHeight: 1,
              color: '#92400E',
              background: '#FEF3C7',
              borderRadius: '3px',
              padding: '1px 3px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {holiday}
            </span>
          </Tooltip>
        )}
        {isResourceColumn ? (<>
          <span style={{
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1.2,
            color: '#1A2332',
            textAlign: 'center',
            maxWidth: '100%',
            overflow: 'hidden',
            display: '-webkit-box',
            // Com o chip de sala embaixo, o nome fica numa linha só (cabeçalho de 48px)
            WebkitLineClamp: resourceRoom ? 1 : 2,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-word',
          }}>
            {resourceLabel}
          </span>
          {resourceRoom && (
            <DayRoomChip summary={resourceRoom} compact readOnly />
          )}
        </>) : (<>
        {/* Dia da semana em cima */}
        <span style={{
          fontSize: '11px',
          fontWeight: 500,
          lineHeight: 1,
          color: isToday ? '#ED3680' : '#1A2332',
          textTransform: 'capitalize',
        }}>
          {date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')}
        </span>

        {/* Número do dia embaixo */}
        <span style={{
          fontSize: '15px',
          fontWeight: 700,
          lineHeight: 1,
          color: isToday ? '#ED3680' : '#1A2332',
        }}>
          {date.getDate()}
        </span>
        </>)}
      </div>

      {/* Corpo do dia */}
      <div
        ref={dayBodyRef}
        style={{
          position: 'relative',
          height: `${totalHeight}px`,
          cursor: 'pointer',
          background: isNonWorkingDay ? '#F1F5F9' : 'transparent',
        }}
        onClick={handleBodyClick}
        onMouseMove={handleBodyMouseMove}
        onMouseLeave={handleBodyMouseLeave}
        onTouchMove={handleBodyTouchMove}
        onTouchEnd={handleBodyTouchEnd}
      >
        {/* Linhas de hora (fundo) */}
        {Array.from({ length: totalHours }, (_, i) => {
          if (isNonWorkingDay) {
            // Dia não trabalhado: apenas a linha divisória, sem card sobreposto
            return (
              <React.Fragment key={startHour + i}>
                <div style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${(i + 1) * slotHeight}px`,
                  height: '1px',
                  background: '#E8EDF2',
                  pointerEvents: 'none',
                }} />
              </React.Fragment>
            );
          }

          // Hora atual (inteira) para verificar se está bloqueada
          const currentHour = startHour + i;
          const isBlocked = blockedHourSet.has(currentHour);

          return (
            <React.Fragment key={startHour + i}>
              {/* Card contido do slot vago — apenas em dias trabalhados.
                  Se a hora estiver bloqueada, não renderiza borda para evitar
                  que a borda do slot vago apareça por baixo do overlay de blocked time. */}
              <div style={{
                position: 'absolute',
                left: '3px',
                right: '3px',
                top: `${i * slotHeight + 2}px`,
                height: `${slotHeight - 4}px`,
                background: 'transparent',
                border: isBlocked ? 'none' : '1px solid #E8EDF2',
                borderRadius: '4px',
                pointerEvents: 'none',
                boxSizing: 'border-box',
              }} />
              {/* Linha divisória entre horas */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${(i + 1) * slotHeight}px`,
                  height: '1px',
                  background: '#F0F2F5',
                  pointerEvents: 'none',
                }}
              />
            </React.Fragment>
          );
        })}

        {/* Profissional com horário próprio: listra o que fica fora das faixas dele */}
        {!isNonWorkingDay && ownHours && offHoursRanges(ownHours, startHour, endHour).map(([from, to]) => (
          <div
            key={`offhours-${from}`}
            style={{
              position: 'absolute',
              left: '3px',
              right: '3px',
              top: `${((from - startHour * 60) / 60) * slotHeight + 2}px`,
              height: `${Math.max(0, ((to - from) / 60) * slotHeight - 4)}px`,
              background: 'repeating-linear-gradient(-45deg, transparent, transparent 8px, rgba(0,0,0,0.04) 8px, rgba(0,0,0,0.04) 9px)',
              borderRadius: '4px',
              pointerEvents: 'none',
              boxSizing: 'border-box',
              zIndex: 1,
            }}
          />
        ))}

        {/* Overlays de horários bloqueados pela empresa (BlockedTimes) — apenas em dias trabalhados */}
        {!isNonWorkingDay && (blockedHours || []).map(hour => {
          if (hour < startHour || hour >= endHour) return null;
          const topPx = (hour - startHour) * slotHeight;
          const isHalfHour = hour % 1 !== 0;
          const heightPx = isHalfHour ? slotHeight * 0.5 : slotHeight;
          return (
            <div
              key={`blockedtime-${hour}`}
              style={{
                position: 'absolute',
                left: '3px',
                right: '3px',
                top: `${topPx + 2}px`,
                height: `${heightPx - 4}px`,
                background: 'repeating-linear-gradient(-45deg, transparent, transparent 8px, rgba(0,0,0,0.04) 8px, rgba(0,0,0,0.04) 9px)',
                borderRadius: '4px',
                pointerEvents: 'none',
                boxSizing: 'border-box',
                zIndex: 1,
              }}
            />
          );
        })}

        {/* Cards de agendamento */}
        {laidOut.map((item) => {
          const {
            appointment, leftPct, nonBlockerAlone, nonBlockerOverlap,
            nbIndex, nbVisible, nbBadgeCount,
          } = item;
          const { topPx, heightPx } = calcCardGeometry(appointment, startHour, slotHeight, slotIntervalMinutes);
          const highlighted = isHighlighted(appointment, highlightedAppointment);
          const highlightStyle = highlighted
            ? { boxShadow: HIGHLIGHT_SHADOW, borderRadius: '4px', zIndex: 6 }
            : {};

          // ── Non-blocker (sozinho no slot ou sobreposto): faixa fina à direita,
          // antes do STRIP do "+". Empilha por prioridade: nbIndex 0 é o mais
          // importante (falta) e fica colado no "+"; reagendado é o primeiro a cair.
          if (nonBlockerAlone || nonBlockerOverlap) {
            // Não coube depois do "+" e dos bloqueantes na largura mínima:
            // não renderiza (a contagem vai no badge "+N" da última faixa visível).
            if (!nbVisible) return null;
            const rightOffset = nonBlockerRightOffset(nbIndex, SLOT_LAYOUT_MOBILE);
            return (
              <div
                key={appointment.id || `${appointment.date}-nb-${nbIndex}`}
                ref={highlighted ? highlightRef : null}
                data-card="true"
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: `${topPx}px`,
                  height: `${heightPx}px`,
                  width: `${SLOT_LAYOUT_MOBILE.NON_BLOCKER_WIDTH_PX}px`,
                  right: `${rightOffset}px`,
                  left: 'auto',
                  boxSizing: 'border-box',
                  ...highlightStyle,
                }}
              >
                <NonBlockerCard
                  appointment={appointment}
                  heightPx={heightPx}
                  onClick={onAppointmentClick}
                  badgeCount={nbBadgeCount}
                  hoverCard={false}
                />
              </div>
            );
          }

          // ── Agendamento bloqueante: divide em partes iguais a área da coluna
          // MENOS rightReserved (faixa do "+" + faixas de non-blocker que couberam).
          // Sempre renderiza e nunca invade a faixa do "+".
          const edges = blockerCardEdges(item, SLOT_LAYOUT_MOBILE.GAP_PX);

          return (
            <div
              key={appointment.id || `${appointment.date}-${leftPct}`}
              ref={highlighted ? highlightRef : null}
              data-card="true"
              data-tour={tourTarget(appointment)}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: `${topPx}px`,
                height: `${heightPx}px`,
                left: edges.left,
                right: edges.right,
                width: 'auto',
                boxSizing: 'border-box',
                ...highlightStyle,
              }}
            >
              <AppointmentCard
                appointment={appointment}
                heightPx={heightPx}
                onClick={onAppointmentClick}
                hoverCard={false}
                hideResource={hideResource}
              />
            </div>
          );
        })}

        {/* Strips de slot — hover visual (pointer-events: none, igual ao desktop) */}
        {!isNonWorkingDay && Array.from({ length: totalHours }, (_, i) => {
          const h = startHour + i;
          const slotsPerHour = Math.floor(60 / slotIntervalMinutes);
          return Array.from({ length: slotsPerHour }, (__, idx) => {
            const m = idx * slotIntervalMinutes;
            const stripKey = `${h}-${m}`;
            const slotTopPx = i * slotHeight + (m / 60) * slotHeight;
            const slotHeightPx = slotHeight * (slotIntervalMinutes / 60);
            const isHovered = hoveredStrip === stripKey;
            return (
              <div
                key={`strip-${h}-${m}`}
                data-slot={stripKey}
                style={{
                  position: 'absolute',
                  top: `${slotTopPx}px`,
                  height: `${slotHeightPx}px`,
                  left: 0,
                  right: 0,
                  width: '100%',
                  pointerEvents: 'none',
                  borderRadius: '3px',
                  background: isHovered ? 'rgba(237, 54, 128, 0.07)' : 'transparent',
                  transition: 'background 0.1s ease',
                  zIndex: 2,
                  boxSizing: 'border-box',
                  border: isHovered ? '1px solid rgba(237, 54, 128, 0.18)' : '1px solid transparent',
                }}
              />
            );
          });
        })}

        {/* Indicador de hora atual */}
        {isToday && (
          <CurrentTimeIndicator startHour={startHour} endHour={endHour} slotHeight={slotHeight} />
        )}
      </div>
    </div>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
const CalendarMobile = ({
  days = [],
  appointments = [],
  startHour = 7,
  endHour = 20,
  onAppointmentClick,
  onEmptySlotClick,
  containerHeight = null,
  availableDaysOfWeek = null,
  blockedTimes = [],
  slotIntervalMinutes = SLOT_INTERVAL_MINUTES_DEFAULT,
  // { id, date, patient } do agendamento recém-salvo — cartão em destaque por alguns segundos
  highlightedAppointment = null,
  // Modo "Profissionais" ([{ id, label, room? }]): um dia, uma coluna por profissional;
  // `room` é a sala do dia (chip só leitura no cabeçalho)
  resourceColumns = null,
  // Horário próprio de cada agenda ({ [id]: Schedule }); a coluna do profissional
  // (ou a do dia, com uma agenda só no filtro) segue as faixas dele
  resourceSchedules = null,
  focusedResourceId = null,
  // Horário da empresa [início, fim] quando outra agenda esticou a grade
  companyHours = null,
  // Lista bruta (sem filtro de situação): decide quais agendas não têm nada no dia
  allAppointments = null,
  // "Localizar profissional": a cada nonce novo rola até a coluna do profissional e a destaca
  focusColumnId = null,
  focusColumnNonce = 0,
}) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Área que rola de lado (e para baixo): é nela que "Localizar profissional" rola
  const areaRef = useRef(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const pendingFocusRef = useRef(0);
  useEffect(() => {
    if (!focusColumnNonce || focusColumnId == null) return undefined;
    pendingFocusRef.current = focusColumnNonce;
    setHighlightedId(focusColumnId);
    const timer = setTimeout(() => setHighlightedId(null), COLUMN_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [focusColumnNonce, focusColumnId]);
  useLayoutEffect(() => {
    if (!pendingFocusRef.current || focusColumnId == null) return;
    const area = areaRef.current;
    const col = area && area.querySelector(`[data-resource-col="${focusColumnId}"]`);
    if (!col) return;
    pendingFocusRef.current = 0;
    const left = col.getBoundingClientRect().left - area.getBoundingClientRect().left + area.scrollLeft - TIME_AXIS_WIDTH_PX;
    area.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  });

  // ── Calcula slotHeight dinamicamente com base na altura disponível ──
  const totalHours = endHour - startHour;
  let slotHeight = DEFAULT_SLOT_HEIGHT; // fallback
  if (containerHeight && containerHeight > 0) {
    const availableForSlots = containerHeight - DAY_HEADER_HEIGHT;
    const calculated = Math.floor(availableForSlots / totalHours);
    slotHeight = Math.min(MAX_SLOT_HEIGHT, Math.max(MIN_SLOT_HEIGHT, calculated));
  }

  const dayPropsFor = (id, day) => columnDayProps({ resourceSchedules, availableDaysOfWeek, blockedTimes, companyHours }, id, day);

  const filterByDay = (list, day) =>
    list.filter(apt => {
      const d = new Date(apt.date);
      return (
        d.getDate() === day.getDate() &&
        d.getMonth() === day.getMonth() &&
        d.getFullYear() === day.getFullYear()
      );
    });
  const getAppointmentsForDay = (day) => filterByDay(appointments, day);
  // Lista bruta para decidir se a agenda tem algo no dia (sem filtro de situação)
  const getAllAppointmentsForDay = (day) => filterByDay(allAppointments || appointments, day);

  return (
    /* Wrapper externo — scroll horizontal e vertical internos; respeita altura do container pai */
    <div data-tour="agenda-week" data-days-area="true" ref={areaRef} style={{
      width: '100%',
      height: '100%',
      overflowX: 'auto',
      overflowY: 'auto',
      background: '#FFFFFF',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'thin',
      scrollbarColor: '#CBD5E1 transparent',
    }}>
      {/* Grid principal: TimeAxis + colunas de dias lado a lado */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        minWidth: 'max-content',
      }}>
        {/* Eixo de horários — sticky à esquerda */}
        <TimeAxisMobile startHour={startHour} endHour={endHour} slotHeight={slotHeight} />

        {/* Modo "Profissionais": colunas de profissionais do mesmo dia */}
        {resourceColumns && days[0] && (() => {
          const day = days[0];
          const dayAppointments = getAppointmentsForDay(day);
          const allDayAppointments = getAllAppointmentsForDay(day);
          const columns = buildResourceColumns(resourceColumns, dayAppointments);
          // Com muitas agendas, as sem nada no dia vão para o fim da fila, sempre
          // visíveis ("Sem profissional" fica onde está)
          const isIdle = (column) => column.id !== UNASSIGNED_COLUMN_ID && !columnHasOwnItems(allDayAppointments, column.id);
          const ordered = columns.length > IDLE_TO_END_MIN_COLUMNS
            ? [...columns.filter((c) => !isIdle(c)), ...columns.filter(isIdle)]
            : columns;
          return ordered.map((column) => (
            <DayColumnMobile
              key={`resource-${column.id}`}
              date={day}
              appointments={dayAppointments.filter((apt) => belongsToColumn(apt, column.id))}
              startHour={startHour}
              endHour={endHour}
              slotHeight={slotHeight}
              onAppointmentClick={onAppointmentClick}
              onEmptySlotClick={onEmptySlotClick}
              today={today}
              {...dayPropsFor(columnResourceId(column), day)}
              slotIntervalMinutes={slotIntervalMinutes}
              highlightedAppointment={highlightedAppointment}
              resourceLabel={column.label}
              resourceId={columnResourceId(column)}
              resourceRoom={column.room}
              hideResource={column.id !== UNASSIGNED_COLUMN_ID}
              highlighted={highlightedId != null && String(column.id) === String(highlightedId)}
            />
          ));
        })()}

        {/* Colunas de dias */}
        {!resourceColumns && days.map(day => {
          return (
            <DayColumnMobile
              key={day.toISOString()}
              date={day}
              appointments={getAppointmentsForDay(day)}
              startHour={startHour}
              endHour={endHour}
              slotHeight={slotHeight}
              onAppointmentClick={onAppointmentClick}
              onEmptySlotClick={onEmptySlotClick}
              today={today}
              {...dayPropsFor(focusedResourceId, day)}
              slotIntervalMinutes={slotIntervalMinutes}
              highlightedAppointment={highlightedAppointment}
              hideResource={focusedResourceId != null}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CalendarMobile;
